import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, getBackendUrl } from "@/lib/api";
import { toast } from "sonner";
import { Play, Info, FloppyDisk, FilePdf, X, BookOpen } from "@phosphor-icons/react";

/* v2.94 · Study helpers shared by every Power System Studies form.
   Kept in one place so all 7 study cards look and feel identical.
   Additions: `useStudyRunner` remembers the last payload; the result
   card now surfaces Save-to-project + Export-PDF actions. */

export const StudyLabel = ({ text, hint }) => (
  <div className="label-tag block mb-1 inline-flex items-center gap-1">
    {text}
    {hint && <span title={hint} className="text-gray-500"><Info size={11} /></span>}
  </div>
);

export const StudyInput = ({ label, hint, ...props }) => (
  <label className="block">
    <StudyLabel text={label} hint={hint} />
    <input
      className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
      {...props}
    />
  </label>
);

export const StudySelect = ({ label, hint, options, ...props }) => (
  <label className="block">
    <StudyLabel text={label} hint={hint} />
    <select
      className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
      {...props}
    >
      {options.map((o) => (
        <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
      ))}
    </select>
  </label>
);

// v2.94 · Save-to-project modal. Fetches the user's projects on open.
function SaveToProjectModal({ open, onClose, kind, payload, result }) {
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      const list = Array.isArray(data) ? data : (data?.projects || []);
      setProjects(list);
      if (list[0]) setSelected(list[0].id);
    } catch (e) {
      toast.error("Failed to load projects");
    } finally { setLoading(false); }
  };

  const save = async () => {
    if (!selected) { toast.error("Pick a project"); return; }
    if (!name.trim()) { toast.error("Give the study a name"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/studies/save-to-project", {
        project_id: selected, name: name.trim(), kind, inputs: payload, result,
      });
      toast.success(`Saved to ${data.project_name}`);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  if (!open) return null;
  if (projects.length === 0 && !loading) load();

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6"
         onClick={onClose} data-testid="save-study-modal">
      <div className="w-full max-w-md bg-[#12151C] border border-white/10 p-5"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="label-tag">// save study</div>
            <h3 className="font-display text-xl font-bold mt-0.5">Save to project</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="save-study-close">
            <X size={16} />
          </button>
        </div>
        <div className="mt-4 space-y-3">
          <label className="block">
            <StudyLabel text="Project" />
            <select value={selected} onChange={(e) => setSelected(e.target.value)}
              disabled={loading}
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
              data-testid="save-study-project">
              {loading && <option value="">Loading…</option>}
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <StudyLabel text="Study name" />
            <input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Feeder LF baseline"
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
              data-testid="save-study-name" />
          </label>
          <div className="flex items-center gap-2 pt-2">
            <button onClick={save} disabled={busy}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
              data-testid="save-study-confirm">
              <FloppyDisk size={14} weight="fill" /> {busy ? "Saving…" : "Save study"}
            </button>
            <button onClick={onClose} className="btn-ghost text-sm py-2" data-testid="save-study-cancel">
              Cancel
            </button>
          </div>
          <p className="text-[10px] text-gray-500 font-mono">
            Re-saving with the same name + kind overwrites the previous entry.
          </p>
        </div>
      </div>
    </div>
  );
}

/* v2.99 · Compact "Load template" dropdown. Fetches per-kind templates
   on mount and applies the selected one via the `onLoad` callback. */
export function TemplateDropdown({ kind, onLoad }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open || loaded) return;
    (async () => {
      try {
        const { data } = await api.get("/studies/templates", { params: { kind } });
        setItems(data?.items || []);
      } catch (e) { setItems([]); }
      setLoaded(true);
    })();
  }, [open, loaded, kind]);

  const apply = (t) => {
    onLoad?.(t.inputs);
    setOpen(false);
    toast.success(`Loaded template "${t.name}"`);
  };

  return (
    <div className="relative" data-testid={`tpl-dropdown-${kind}`}>
      <button onClick={() => setOpen(!open)}
        className="text-[10px] font-mono px-3 py-1.5 border border-white/10 text-gray-400 hover:text-[#00E5FF] hover:border-[#00E5FF]/40 inline-flex items-center gap-1"
        data-testid={`tpl-open-${kind}`}
        title="Seed this study with a pre-configured template">
        <BookOpen size={12} /> load template
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 w-80 bg-[#12151C] border border-white/10 shadow-2xl"
             onMouseLeave={() => setOpen(false)}
             data-testid={`tpl-menu-${kind}`}>
          {items.length === 0 && (
            <div className="p-3 text-[10px] font-mono text-gray-500">no templates for {kind}</div>
          )}
          {items.map((t) => (
            <button key={t.id} onClick={() => apply(t)}
              className="w-full text-left p-3 hover:bg-white/5 border-b border-white/5 last:border-0"
              data-testid={`tpl-item-${t.id}`}>
              <div className="text-white text-sm font-mono">{t.name}</div>
              <div className="text-gray-500 text-[10px] mt-0.5">{t.description}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export const StudyResultCard = ({ result, error, kind, payload, name }) => {
  const [saveOpen, setSaveOpen] = useState(false);

  const exportPdf = async () => {
    try {
      const res = await fetch(`${getBackendUrl()}/api/studies/export-pdf`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name || "Study", kind, inputs: payload, result,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `study_${(name || "study").replace(/\W+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF generated");
    } catch (e) {
      toast.error("PDF export failed");
    }
  };

  if (error) return (
    <div className="mt-6 border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-4 text-sm text-[#FF8A85] font-mono" data-testid="study-error">
      {error}
    </div>
  );
  if (!result) return null;
  return (
    <>
      <div className="mt-6 border border-[#00E5FF]/30 bg-[#0A0C10] p-4 font-mono text-sm" data-testid="study-result">
        <div className="flex items-center justify-between mb-2">
          <div className="label-tag">// result</div>
          {kind && payload && (
            <div className="flex items-center gap-1" data-testid="study-actions">
              <button onClick={() => setSaveOpen(true)}
                className="text-[10px] text-[#00E5FF] hover:underline inline-flex items-center gap-1 px-2 py-1 border border-white/10 hover:border-[#00E5FF]/40"
                data-testid="study-save-btn"
                title="Save this study to a project">
                <FloppyDisk size={10} /> save
              </button>
              <button onClick={exportPdf}
                className="text-[10px] text-gray-300 hover:text-white inline-flex items-center gap-1 px-2 py-1 border border-white/10 hover:border-white/30"
                data-testid="study-pdf-btn"
                title="Export a stampable PE PDF">
                <FilePdf size={10} /> pdf
              </button>
            </div>
          )}
        </div>
        <pre className="whitespace-pre-wrap text-[11px] leading-relaxed text-gray-200 max-h-[500px] overflow-auto">
          {JSON.stringify(result, null, 2)}
        </pre>
      </div>
      <SaveToProjectModal open={saveOpen} onClose={() => setSaveOpen(false)}
        kind={kind} payload={payload} result={result} />
    </>
  );
};

/* Generic "Compute" button + async wrapper — every study card uses it.
   v2.94: also tracks the last payload/kind so downstream components
   (StudyResultCard) can save or export without re-plumbing. */
export function useStudyRunner(endpoint, testid = "study-run") {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const kind = endpoint.replace(/^\/studies\//, "").replace(/-/g, "_");

  const run = async (p) => {
    setBusy(true); setError(""); setResult(null); setPayload(p);
    try {
      const { data } = await api.post(endpoint, p);
      setResult(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Study failed");
      toast.error("Study failed — check inputs.");
    } finally {
      setBusy(false);
    }
  };
  const Button = ({ onClick, disabled }) => (
    <button onClick={onClick} disabled={busy || disabled}
      className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
      data-testid={testid}>
      <Play size={14} weight="fill" /> {busy ? "Running…" : "Run study"}
    </button>
  );
  return { busy, result, error, run, Button, payload, kind };
}
